import React, { useEffect, useState, useRef } from 'react'
import { createFormBag, FormBag, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore } from 'domains/org/getty/store'

import { VisitorInfoSiteContent, VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { CreateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, Invariant } from 'helpers/core'
import { Status } from 'helpers/status'

import { SelectPageType } from './select-page-type'
import { CreatePage } from './create-page'
import { visitorInfoSchema } from '../../../schemas/visitorInfo'

type Step = {
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
};

export type Steps = {
  selectPageType: Step;
  createContentPage: Step;
  createImagePage: Step;
  createHTMLPage: Step;
  createURLPage: Step;
  createAppPage: Step;
  createDeepDivePage: Step;
};

export type FormValues = {
  title: string;
};

export const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values.title.trim()) {
    errors.title = 'Title required';
  }
  return errors;
};

type ConnectedProps = {
  visitorInfoCreate: GettyStore['getty']['visitorInfoCreate'];
}

type ActionProps = Pick<GettyActionCreators, 'gettyCreateVisitorInfoPageRequest'>;

type DirectProps = {
  readonly onCancel: () => void;
  readonly projectId: string;
  readonly historyPush: (id: string) => void;
  readonly siteContentRef: VisitorInfoSiteContent.Ref;
}

type Props = ConnectedProps & ActionProps & DirectProps;

const VisitorInfoCreateView = (props: Props) => {
  const {
    gettyCreateVisitorInfoPageRequest,
    visitorInfoCreate,
    siteContentRef,
    historyPush,
    onCancel,
    projectId,
  } = props

  const [ selectedType, setSelectedType ] = useState<keyof Steps>()
  const [ currentStep, setCurrentStep ] = useState<keyof Steps>('selectPageType')
  const [ bag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ title: '' }))

  const hasSubmitted = useRef(false)

  const updateSelectedType = (type: keyof Steps) => {
    // available target types get loaded from the backend, so check we can handle them.
    if (!steps[type]) {
      throw new Invariant('unhandled')
    }
    setSelectedType(type)
  }

  useEffect(() => {
    if(visitorInfoCreate.status === Status.Ready && hasSubmitted.current && VisitorInfoPage.mustRef(visitorInfoCreate.data.ref)) {
      hasSubmitted.current = false
      historyPush(`/project/${projectId}/visitor-info/${VisitorInfoPage.mustRef(visitorInfoCreate.data.ref).id}`)
    }
  }, [visitorInfoCreate, historyPush, projectId])

  const submit = (pageProperties: VisitorInfoPage.Entity['pageProperties']) => {
    const validatedBag = validateFormBag(bag, validate)
    setFormBag(validatedBag)

    if (validatedBag.valid) {
      hasSubmitted.current = true
      gettyCreateVisitorInfoPageRequest({
        ...CreateVisitorInfoPageRequest.defaults,
        siteContentRef,
        listTitle: visitorInfoSchema.listTitle ? visitorInfoSchema.listTitle.write(bag.values.title) : bag.values.title,
        pageProperties,
      })
    }
  }

  const steps: Steps = {
    selectPageType: {
      header: <h3>Create a new page</h3>,
      body: (
        <SelectPageType
          selectedType={selectedType}
          setSelectedType={updateSelectedType}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            onClick={() => selectedType && setCurrentStep(selectedType)}
            isDisabled={!selectedType}
          >
            Continue
          </Button>
        </ButtonGroup>
      )
    },
    createContentPage: {
      header: <h3>Create Content page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.AmenityCategory.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
    createImagePage: {
      header: <h3>Create Image page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.SiteMap.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
    createHTMLPage: {
      header: <h3>Create HTML page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.StaticWebPage.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
    createURLPage: {
      header: <h3>Create URL page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.URLWebPage.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
    createAppPage: {
      header: <h3>Create App page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.AppPage.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
    createDeepDivePage: {
      header: <h3>Create Deep Dive page</h3>,
      body: (
        <CreatePage
          bag={bag}
          setFormBag={setFormBag}
          visitorInfoCreate={visitorInfoCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => submit(VisitorInfoPage.DeepDivePage.defaults)}
          >
            Create Page
          </Button>
        </ButtonGroup>
      )
    },
  }

  return (
    <Modal
      onClose={onCancel}
      header={steps[currentStep].header}
      footer={steps[currentStep].footer}
    >
      {steps[currentStep].body}
    </Modal>
  )
}

export const VisitorInfoCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    visitorInfoCreate: store.getty.visitorInfoCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateVisitorInfoPageRequest'),
  }
)(VisitorInfoCreateView)
